import { useMemo, useState } from "react";
import {
    Button,
    addToast,
    Chip,
    Divider,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Checkbox,
    Input,
    CheckboxGroup,
    Card,
    CardBody,
    Badge,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLink,
    faTrash,
    faFileImport,
    faSearch,
    faShare,
    faCopy,
} from "@fortawesome/free-solid-svg-icons";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Connection, UserConnection } from "@/types/connection";
import { Platform, Status } from "@/types/enums";
import useConfirm from "@/lib/confirm/useConfirm";
import { useTranslation } from "react-i18next";
import johnDeereLogo from "@/assets/images/JDOC-tm-default.svg";
import caseIhLogo from "@/assets/images/CaseNewHolland_CombinedLogos.png";
import climateFieldviewLogo from "@/assets/images/Climate_FieldView_Horizontal_thumb.svg";
import { User } from "@/types/user";
import fieldnetLogo from "@/assets/images/fieldnet-logo.svg";
import { sortArrayByKey } from "@/lib/utils/sorting";

export const Route = createFileRoute("/dashboard/account-settings/connections")(
    {
        component: Connections,
    },
);

interface PlatformConfig {
    logo: string;
    authUrl?: string;
}

const PLATFORMS: Record<Platform, PlatformConfig> = {
    [Platform.JohnDeere]: {
        logo: johnDeereLogo,
        authUrl: "connections/john_deere_auth_url/",
    },
    [Platform.CNHI]: {
        logo: caseIhLogo,
        authUrl: "leaf/cnhi_auth_url/",
    },
    [Platform.FieldView]: {
        logo: climateFieldviewLogo,
        authUrl: "connections/fieldview_auth_url/",
    },
    [Platform.FieldNET]: {
        logo: fieldnetLogo,
        authUrl: "connections/fieldnet_auth_url/",
    },
};

function Connections() {
    const { t } = useTranslation("messages");
    const { useGetRequest, useMutateRequest } = useRequest();
    const { user, isConsultantOrSentinel } = useAuth();
    const navigate = useNavigate();
    const { showConfirm } = useConfirm();

    const connectionsQuery = useGetRequest<Connection[]>({
        url: "/connections/",
        method: "get",
    });

    const connectionOptions = useMemo(() => {
        return (
            connectionsQuery.data?.map((connection) => {
                return {
                    ...connection,
                    ...PLATFORMS[connection.platform],
                };
            }) ?? []
        );
    }, [connectionsQuery.data]);

    const userConnectionQuery = useGetRequest<UserConnection[]>({
        url: `/user_connections/?user=${user?.id}`,
        method: "get",
        enabled: !!user?.id,
    });

    const getPlatformAuthUrl = useMutateRequest<string>({
        method: "get",
        errorMessage: t("error.failed_to_get_authentication_url_for_platform"),
    });

    const handlePlatformAuth = async (platform: Platform) => {
        getPlatformAuthUrl.mutate(
            {
                url: PLATFORMS[platform]?.authUrl,
            },
            {
                onSuccess: (data) => {
                    window.open(data, "_self");
                },
                onError: () => {
                    addToast({
                        title: t(
                            "addToast.failed_to_get_authentication_url_for_platform",
                            {
                                platformName: platform,
                            },
                        ),
                        color: "danger",
                        timeout: 3000,
                    });
                },
            },
        );
    };

    const connectionData = useMemo(() => {
        return (userConnectionQuery.data ?? []).reduce(
            (acc, connection) => {
                acc[connection.platform] = connection;
                return acc;
            },
            {} as Record<Platform, UserConnection>,
        );
    }, [userConnectionQuery.data]);

    const deleteConnection = useMutateRequest({
        method: "delete",
        errorMessage: t("error.failed_to_delete_connection"),
        successMessage: t("success.connection_deleted_successfully"),
        onSuccess: () => {
            userConnectionQuery.refetch();
        },
    });

    const handleDeleteConnection = async (connection: UserConnection) => {
        showConfirm({
            title: t("connections.delete_connection", {
                ns: "accountSettings",
            }),
            description: t("connections.delete_connection_description", {
                ns: "accountSettings",
            }),
            dismissable: true,
            color: "danger",
            onConfirm: async () => {
                await deleteConnection.mutateAsync({
                    url: `/user_connections/${connection.id}/`,
                });
            },
        });
    };

    const usersRequest = useGetRequest<User[]>({
        url: "/users/",
        method: "get",
    });
    const [search, setSearch] = useState("");
    const userOptions = useMemo(() => {
        return sortArrayByKey(usersRequest.data ?? [], "full_name").filter(
            (user) => {
                return user.full_name
                    ?.toLowerCase()
                    .includes(search.toLowerCase());
            },
        );
    }, [usersRequest.data, search]);
    const [invitePlatform, setInvitePlatform] = useState<number | null>(null);
    const [inviteUsers, setInviteUsers] = useState<string[]>([]);
    const inviteUsersRequest = useMutateRequest({
        method: "post",
        url: `/connections/${invitePlatform}/invite/`,
        errorMessage: t("error.failed_to_invite_users"),
        successMessage: t("success.users_invited_successfully"),
    });
    const handleInviteUsers = async () => {
        inviteUsersRequest.mutate(
            {
                data: {
                    users: inviteUsers,
                },
            },
            {
                onSuccess: () => {
                    setInvitePlatform(null);
                    setInviteUsers([]);
                },
            },
        );
    };

    return (
        <div className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 max-w-[50rem]">
                <h1 className="text-2xl font-bold">
                    {t("connections.title", { ns: "accountSettings" })}
                </h1>
                <Button
                    color="primary"
                    onPress={() =>
                        navigate({ to: "/dashboard/fields/add" })
                    }
                    startContent={<FontAwesomeIcon icon={faFileImport} />}
                    className="self-start sm:self-auto"
                >
                    {t("connections.import_field", {
                        ns: "accountSettings",
                    })}
                </Button>
            </div>
            <Divider className="mb-4 max-w-[50rem]" />
            <div className="space-y-4 mb-8 max-w-[50rem]">
                {connectionOptions.map((connection) => {
                    const activeConnection =
                        connectionData[connection.platform];
                    return (
                        <div
                            key={connection.id}
                            className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-white rounded-lg shadow-sm border border-gray-200 px-4 sm:px-6 py-4"
                        >
                            <div className="flex items-center gap-2">
                                <img
                                    src={connection.logo}
                                    alt={connection.platform}
                                    className="h-14 w-auto object-contain"
                                    style={{ maxWidth: 160 }}
                                />
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {isConsultantOrSentinel &&
                                    connection.status === Status.Active && (
                                        <>
                                            <Button
                                                size="sm"
                                                onPress={() => {
                                                    getPlatformAuthUrl.mutate(
                                                        {
                                                            url: PLATFORMS[connection.platform]?.authUrl,
                                                        },
                                                        {
                                                            onSuccess: (data) => {
                                                                navigator.clipboard.writeText(data);
                                                            },
                                                        },
                                                    );
                                                }}
                                                isLoading={getPlatformAuthUrl.isPending}
                                                startContent={
                                                    <FontAwesomeIcon icon={faCopy} />
                                                }
                                            >
                                                Copy Link
                                            </Button>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onPress={() =>
                                                    setInvitePlatform(connection.id)
                                                }
                                                startContent={
                                                    <FontAwesomeIcon
                                                        icon={faShare}
                                                    />
                                                }
                                            >
                                                Invite Users
                                            </Button>
                                        </>
                                    )}
                                {activeConnection &&
                                    !activeConnection.action_required ? (
                                    <Button
                                        isIconOnly
                                        color="danger"
                                        size="sm"
                                        onPress={() =>
                                            handleDeleteConnection(
                                                activeConnection,
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                ) : activeConnection?.action_required ? (
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Chip
                                            color="danger"
                                            variant="flat"
                                            radius="sm"
                                        >
                                            Please Reconnect
                                        </Chip>
                                        <Badge color="danger" content="">
                                            <Button
                                                size="sm"
                                                color="primary"
                                                onPress={() => {
                                                    handlePlatformAuth(
                                                        connection.platform,
                                                    );
                                                }}
                                                isLoading={
                                                    getPlatformAuthUrl.isPending
                                                }
                                                startContent={
                                                    <FontAwesomeIcon
                                                        icon={faLink}
                                                        className="h-4 w-4"
                                                    />
                                                }
                                            >
                                                {t("connections.connect", {
                                                    ns: "accountSettings",
                                                })}
                                            </Button>
                                        </Badge>
                                    </div>
                                ) : (
                                    <Button
                                        size="sm"
                                        color="primary"
                                        onPress={() =>
                                            handlePlatformAuth(
                                                connection.platform,
                                            )
                                        }
                                        isLoading={getPlatformAuthUrl.isPending}
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={faLink}
                                                className="h-4 w-4"
                                            />
                                        }
                                    >
                                        {t("connections.connect", {
                                            ns: "accountSettings",
                                        })}
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <Modal
                isOpen={invitePlatform !== null}
                onClose={() => setInvitePlatform(null)}
                scrollBehavior="inside"
                className="h-[80vh]"
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-2">
                        Invite Users to Connect
                        <Input
                            aria-label="Search"
                            placeholder="Search"
                            size="sm"
                            startContent={<FontAwesomeIcon icon={faSearch} />}
                            onValueChange={setSearch}
                        />
                        <Divider />
                    </ModalHeader>
                    <ModalBody className="flex flex-col gap-4 overflow-y-auto">
                        <CheckboxGroup
                            value={inviteUsers}
                            onValueChange={setInviteUsers}
                        >
                            {userOptions.map((user) => (
                                <Card key={user.id} className="w-full text-sm">
                                    <CardBody>
                                        <Checkbox value={user.username}>
                                            {
                                                <div className="flex flex-col">
                                                    <p className="font-semibold">
                                                        {user.full_name}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {user.username}
                                                    </p>
                                                </div>
                                            }
                                        </Checkbox>
                                    </CardBody>
                                </Card>
                            ))}
                        </CheckboxGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="primary"
                            onPress={() => handleInviteUsers()}
                            isDisabled={inviteUsers.length === 0}
                        >
                            {t("connections.invite", { ns: "accountSettings" })}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div >
    );
}
